import classNames from 'classnames'
import {
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
  max as maxDate,
  min as minDate,
} from 'date-fns'

import { EMPTY_LIST } from '../../../constants/common'
import { ClickAction } from '../../Action'
import { MonthDay } from '../MonthDay'

import * as styles from './DateSelectOption.module.css'

/**
 * @typedef {ClickActionProps} DateSelectOption
 *
 * @property {React.ComponentType<ClickActionComponentProps>} props.Component
 * @property className
 * @property disabled
 * @property disabledClassName
 * @property minValue
 * @property maxValue
 * @property selected
 * @property selectedClassName
 * @property {Date} props.value
 */

function DateSelectOption({
  Component = MonthDay,
  className = styles.DateSelectOption,
  disabled = false,
  disabledClassName = styles.disabled,
  inRangeClassName = styles.inRange,
  maxValue,
  minValue,
  rangeEnd,
  rangeEndClassName = styles.rangeEnd,
  rangeHoverEnd,
  rangeStart,
  rangeStartClassName = styles.rangeStart,
  selected = EMPTY_LIST,
  selectedClassName = styles.selected,
  value,
  onRangeHoverEndChange,
  ...componentProps
}) {
  const isDisabled =
    disabled ||
    (minValue != null && isBefore(value, minValue)) ||
    (maxValue != null && isAfter(value, maxValue))

  const previewEnd = rangeEnd ?? rangeHoverEnd
  const hasPreviewRange = !!rangeStart && !!previewEnd

  const rangeMin = hasPreviewRange ? minDate([rangeStart, previewEnd]) : null
  const rangeMax = hasPreviewRange ? maxDate([rangeStart, previewEnd]) : null

  const isRangeStart = !!rangeStart && isSameDay(rangeStart, value)
  const isRangeEnd = !!rangeEnd && isSameDay(rangeEnd, value)

  const isInRange =
    hasPreviewRange &&
    isWithinInterval(value, { end: rangeMax, start: rangeMin })

  function handleMouseEnter() {
    if (!isDisabled && rangeStart && !rangeEnd) {
      onRangeHoverEndChange?.(value)
    }
  }

  const componentClassName = classNames(className, {
    [disabledClassName]: isDisabled,
    [inRangeClassName]: isInRange,
    [rangeEndClassName]: isRangeEnd,
    [rangeStartClassName]: isRangeStart,
    [selectedClassName]: selected.some(selectedDate =>
      isSameDay(selectedDate, value)
    ),
  })

  return (
    <ClickAction
      {...componentProps}
      Component={Component}
      className={componentClassName}
      disabled={isDisabled}
      value={value}
      onMouseEnter={handleMouseEnter}
    />
  )
}

export default DateSelectOption
